import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { useCart } from "../lib/cart";
import { brandFor, darkLogoFor } from "../lib/brand";
import Navbar from "../components/Navbar";
import { ChevronLeft, Plus, Minus, ShoppingCart, Flame } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Shell>
        <div className="card-elev p-8 text-center text-sm" style={{ color: "var(--text-mute)" }}>Načítavanie...</div>
      </Shell>
    );
  }
  if (!product) {
    return (
      <Shell>
        <div className="card-elev p-8 text-center">
          <p className="text-sm mb-3">Produkt sa nenašiel.</p>
          <Link to="/catalog" className="btn-primary"><ChevronLeft size={12} /> Späť do katalógu</Link>
        </div>
      </Shell>
    );
  }

  let stockClass, stockLabel;
  if (product.on_promo) {
    stockClass = "stock-in"; stockLabel = "Ihneď dostupné";
  } else if (product.stock <= 0) {
    stockClass = "stock-low"; stockLabel = "Na objednávku · dodanie 2–3 dni";
  } else if (product.available_immediate) {
    stockClass = "stock-in"; stockLabel = `Na sklade · ${product.stock} ks (ihneď)`;
  } else if (product.stock < 5) {
    stockClass = "stock-low"; stockLabel = `Objednávka 2–3 dni · ${product.stock} ks`;
  } else {
    stockClass = "stock-low"; stockLabel = "Objednávka 2–3 dni";
  }
  const noPrice = !product.your_price || product.your_price === 0;
  const brand = brandFor(product);
  const onPromo = !noPrice && product.on_promo && product.your_list_price && product.your_list_price > product.your_price;
  const discountPct = onPromo ? Math.round((1 - product.your_price / product.your_list_price) * 100) : 0;
  const gallery = product.image_urls?.length
    ? product.image_urls
    : (product.image_url ? [product.image_url] : []);
  const mainImg = gallery[Math.min(imgIdx, gallery.length - 1)] || null;

  return (
    <Shell brandLabel={brand?.label} productName={product.name}>
      <div className="grid lg:grid-cols-[420px_1fr] gap-6 items-start">
        <div className="card-elev p-5">
          <div
            className="aspect-square flex flex-col items-center justify-center relative bg-white border p-6"
            style={{ borderColor: "var(--border)" }}
          >
            {brand && (
              <div
                className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded"
                style={{ background: "var(--surface-elev)", border: "1px solid var(--border)" }}
              >
                <img
                  src={darkLogoFor(brand)}
                  alt={brand.label}
                  className="h-4 w-4 object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-mute)" }}>
                  {brand.label}
                </span>
              </div>
            )}
            {mainImg ? (
              <>
                <img
                  src={mainImg}
                  alt={product.name}
                  className="max-h-[280px] max-w-full object-contain"
                  style={product.sold ? { filter: "grayscale(0.8)", opacity: 0.7 } : undefined}
                  data-testid="detail-main-image"
                />
                <div className="font-mono font-bold text-lg text-center break-all mt-3" style={{ color: "var(--brand)" }}>
                  {product.sku}
                </div>
              </>
            ) : (
              <>
                <div className="text-[11px] uppercase font-bold mb-3" style={{ color: "var(--text-mute)" }}>Kód produktu</div>
                <div
                  className="font-mono font-bold text-3xl sm:text-4xl text-center break-all"
                  style={{ color: "var(--brand)", letterSpacing: "0.03em" }}
                >
                  {product.sku}
                </div>
              </>
            )}
            {product.sold && (
              <div className="absolute inset-0 flex items-center justify-center" data-testid="detail-sold-overlay">
                <span
                  className="px-6 py-2 text-lg font-bold uppercase tracking-widest rotate-[-8deg]"
                  style={{ background: "#c8102e", color: "#fff", boxShadow: "0 2px 10px rgba(0,0,0,0.3)" }}
                >
                  Predané
                </span>
              </div>
            )}
            {onPromo && (
              <div className="mt-5 inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase" style={{ background: "#c8102e", color: "#fff", borderRadius: 3 }} data-testid="detail-promo-badge">
                <Flame size={12} /> Akcia · −{discountPct}%
              </div>
            )}
            {product.on_promo && product.promo_label && (
              <div className="mt-2 text-[10px] uppercase tracking-wider font-bold text-center" style={{ color: "#c8102e" }}>
                {product.promo_label}
              </div>
            )}
          </div>

          {/* Gallery thumbnails */}
          {gallery.length > 1 && (
            <div className="flex gap-2 mt-3 flex-wrap" data-testid="detail-gallery-thumbs">
              {gallery.map((url, i) => (
                <button
                  key={url}
                  onClick={() => setImgIdx(i)}
                  className="w-16 h-16 border-2 flex items-center justify-center bg-white"
                  style={{ borderColor: i === imgIdx ? "var(--brand)" : "var(--border)" }}
                  data-testid={`detail-thumb-${i}`}
                >
                  <img src={url} alt="" className="max-w-full max-h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div>
            {brand && (
              <div className="text-xs uppercase font-bold mb-1" style={{ color: "var(--text-mute)" }}>
                {brand.label} · OEM diel
              </div>
            )}
            <h1 className="text-2xl font-bold uppercase tracking-tight" data-testid="detail-name">{product.name}</h1>
            <div className="border-b-2 mt-2 mb-3" style={{ borderColor: "var(--brand)" }} />

            <div className="flex items-center gap-2 flex-wrap">
              <span className="sku-pill" data-testid="detail-sku">Kód: {product.sku}</span>
              <span className={`stock-badge ${stockClass}`}>{stockLabel}</span>
              {product.category && <span className="tier-badge">{product.category}</span>}
            </div>
          </div>

          {product.description && (
            <div className="card-elev">
              <div className="section-title">Popis</div>
              <div className="p-4 text-sm leading-relaxed" data-testid="detail-description" style={{ color: "var(--text)" }}>
                {product.description}
              </div>
            </div>
          )}

          {/* Price + add */}
          <div className="card-elev">
            <div className="section-title">Vaša cena</div>
            <div className="p-5 flex flex-wrap items-end gap-5">
              <div>
                <div className="text-xs uppercase font-bold mb-1" style={{ color: "var(--text-mute)" }}>
                  Zmluvná netto cena
                </div>
                {noPrice ? (
                  <div className="text-xl font-bold" style={{ color: "var(--text-mute)" }}>Ponuka na vyžiadanie</div>
                ) : onPromo ? (
                  <div>
                    <div className="text-3xl font-bold" data-testid="detail-your-price" style={{ color: "#c8102e" }}>
                      {formatMoney(product.your_price)}
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-sm line-through" style={{ color: "var(--text-disabled)" }}>{formatMoney(product.your_list_price)}</span>
                      <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: "#c8102e", color: "#fff" }}>−{discountPct}%</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-3xl font-bold" data-testid="detail-your-price" style={{ color: "var(--brand)" }}>
                    {formatMoney(product.your_price)}
                  </div>
                )}
                <div className="text-[10px] mt-1" style={{ color: "var(--out-stock)" }}>
                  Cena je NETTO, bez DPH.
                </div>
              </div>

              <div className="flex items-stretch gap-2 ml-auto">
                <div className="inline-flex items-center border" style={{ borderColor: "var(--border-hi)", borderRadius: 3, background: "#fff" }}>
                  <button
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="w-9 h-9 hover:bg-[color:var(--surface-elev)]"
                    data-testid="qty-decrement"
                  ><Minus size={14} /></button>
                  <input
                    type="number" min="1" value={qty}
                    onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-12 text-center bg-transparent outline-none font-bold border-x"
                    style={{ borderColor: "var(--border-hi)", height: 36 }}
                    data-testid="qty-input"
                  />
                  <button
                    onClick={() => setQty((q) => q + 1)}
                    className="w-9 h-9 hover:bg-[color:var(--surface-elev)]"
                    data-testid="qty-increment"
                  ><Plus size={14} /></button>
                </div>
                <button
                  onClick={() => { add(product, qty); toast.success(`${qty} × ${product.sku} pridané do košíka`); }}
                  disabled={noPrice || product.sold}
                  className="btn-primary"
                  data-testid="detail-add-to-cart"
                >
                  <ShoppingCart size={14} />
                  {product.sold ? "Predané" : noPrice ? "Vyžiadať ponuku" : `Do košíka`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children, brandLabel, productName }) {
  return (
    <div>
      <Navbar />
      <div className="breadcrumb">
        <div className="max-w-[1200px] mx-auto px-4">
          <Link to="/catalog">Domov</Link> / <Link to="/catalog">Katalóg</Link>
          {brandLabel && <> / <span>{brandLabel}</span></>}
          {productName && <> / <span style={{ color: "var(--text)" }}>{productName.length > 50 ? productName.slice(0, 50) + "..." : productName}</span></>}
        </div>
      </div>
      <main className="max-w-[1200px] mx-auto px-4 py-6">
        <Link to="/catalog" className="inline-flex items-center gap-1 text-xs font-bold uppercase mb-4 hover:underline" style={{ color: "var(--brand)" }} data-testid="back-to-catalog">
          <ChevronLeft size={14} /> Späť do katalógu
        </Link>
        {children}
      </main>
    </div>
  );
}
